import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Main from "./ServiceStep/Main";
import Form from "./ServiceStep/Form";
import api from "../../../utils/axios";
import Swal from "sweetalert2";

const ServiceStep = () => {
  const location = useLocation();
  const [mode, setMode] = useState("");
  const [serviceList, setServiceList] = useState([]);
  const [stepList, setStepList] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [formData, setFormData] = useState({});
  const companyId = useMemo(() => localStorage.getItem("company") || "", []);

  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle service selection from navigation state (when returning from Service page)
  useEffect(() => {
    if (location.state?.selectServiceId && serviceList.length > 0) {
      const serviceToSelect = serviceList.find(
        (s) => s.Service_Id === location.state.selectServiceId
      );
      if (serviceToSelect) {
        setSelectedService(serviceToSelect);
        fetchSteps(serviceToSelect.Service_Id);
        // Clear the state to avoid re-selecting on re-render
        window.history.replaceState({}, document.title);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.selectServiceId, serviceList]);

  // Handle return to form from StepUnit page
  useEffect(() => {
    if (location.state?.returnToForm && location.state?.formData && location.state?.mode) {
      // Restore selectedService first if available
      if (location.state?.selectedService) {
        setSelectedService(location.state.selectedService);
        // Fetch steps for the service
        fetchSteps(location.state.selectedService.Service_Id);
      }
      // Restore mode and formData
      setMode(location.state.mode);
      setFormData(location.state.formData);
      // Note: formDataList will be passed as prop to Form component
      // Don't clear state here, let Form component handle it
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.returnToForm]);

  const initialize = async () => {
    try {
      setMode("S");
      // Don't reset selectedService if we're returning from Service page with a service to select
      if (!location.state?.selectServiceId) {
        setSelectedService(null);
      }
      setSelectedStep(null);
      setFormData({});
      await fetchServices();
    } catch (error) {
      Swal.fire("ผิดพลาด!", error.message || "ไม่สามารถโหลดข้อมูลได้", "error");
    }
  };

  const fetchServices = async () => {
    try {
      const res = await api.get(`/get-services?Comp_Id=${companyId || ""}`);
      if (res.data.status === "OK") {
        setServiceList(res.data.result || []);
      } else {
        setServiceList([]);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
      Swal.fire("ผิดพลาด!", "ไม่สามารถโหลดข้อมูลบริการได้", "error");
      setServiceList([]);
    }
  };

  const fetchSteps = async (serviceId) => {
    try {
      const res = await api.get(`/get-service-steps/${serviceId}`);
      if (res.data.status === "OK") {
        setStepList(res.data.result || []);
      } else {
        setStepList([]);
      }
    } catch (error) {
      console.error("Error fetching steps:", error);
      Swal.fire("ผิดพลาด!", "ไม่สามารถโหลดข้อมูลขั้นตอนการบริการได้", "error");
      setStepList([]);
    }
  };

  const onServiceSelected = async (service) => {
    setSelectedService(service);
    await fetchSteps(service.Service_Id);
  };

  const onPageInsert = () => {
    if (!selectedService) {
      Swal.fire("แจ้งเตือน", "กรุณาเลือกบริการก่อน", "warning");
      return;
    }
    setSelectedStep(null);
    setFormData({
      Service_Id: selectedService.Service_Id,
      Step_Title: "",
      Step_Description: "",
      Step_Order: stepList.length > 0 ? Math.max(...stepList.map(s => s.Step_Order || 0)) + 1 : 1,
      Step_Qty: "",
      Step_Unit: "",
      Step_Item_Id: null,
      IsDefault: 0,
      IsActive: 1,
      Comp_Id: companyId || undefined,
    });
    setMode("I");
  };

  const onRowSelected = (step) => {
    if (!step) {
      Swal.fire("แจ้งเตือน", "ไม่พบข้อมูลขั้นตอนการบริการที่เลือก", "warning");
      return;
    }

    setMode("U");
    setFormData({
      Step_Id: step.Step_Id,
      Service_Id: step.Service_Id,
      Step_Title: step.Step_Title,
      Step_Description: step.Step_Description || "",
      Step_Order: step.Step_Order || 0,
      Step_Qty: step.Step_Qty || "",
      Step_Unit: step.Step_Unit || "",
      Step_Item_Id: step.Step_Item_Id || null,
      IsDefault: step.IsDefault !== undefined ? step.IsDefault : 0,
      IsActive: step.IsActive !== undefined ? step.IsActive : 1,
      Comp_Id: step.Comp_Id || companyId || undefined,
    });
    setSelectedStep(step);
  };

  const goBackToMain = async () => {
    try {
      setMode("S");
      setSelectedStep(null);
      setFormData({});
      // Refresh steps for the selected service if one is selected
      if (selectedService) {
        await fetchSteps(selectedService.Service_Id);
      }
    } catch (error) {
      console.error("Error going back to main:", error);
    }
  };

  return (
    <div className="ServiceStep">
      <div className="wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="container">
            <div className="page-inner">
              {mode === "S" ? (
                <Main
                  serviceList={serviceList}
                  stepList={stepList}
                  selectedService={selectedService}
                  onServiceSelected={onServiceSelected}
                  onPageInsert={onPageInsert}
                  onRowSelected={onRowSelected}
                  name={"ขั้นตอนการบริการ"}
                />
              ) : (
                <Form
                  callInitialize={initialize}
                  goBackToMain={goBackToMain}
                  mode={mode}
                  name={"ขั้นตอนการบริการ"}
                  formData={formData}
                  setFormData={setFormData}
                  selectedStep={selectedStep}
                  selectedService={selectedService}
                  setMode={setMode}
                  stepList={stepList}
                  returnToFormData={location.state?.formDataList}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceStep;

